import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyLiveEngagementState,
  initialAcademyLiveEngagementState,
} from './entities/academy-live-engagement-store.model';
import { LiveAttendanceService } from './infra/live-attendance.service';
import { LiveEventsService } from './infra/live-events.service';
import { LiveSubscriptionsService } from './infra/live-subscriptions.service';
import { LiveRatingsService } from './infra/live-ratings.service';

const ALL_OPS = [
  'vodJoin',
  'reportVodProgress',
  'getResume',
  'subscribe',
  'unsubscribe',
  'rate',
  'loadAttendances',
] as const;

/**
 * One page of the producer roster. The server caps `top` at 200 (422 above) and sorts open
 * sessions first, so this page always carries everyone still watching up to that size;
 * the panel signals when `Total` goes beyond it.
 */
const ATTENDANCES_PAGE_SIZE = 200;

interface ReportVodProgressInput {
  liveEventId: string;
  positionSeconds: number;
}

interface RateInput {
  liveEventId: string;
  score: number;
  comment?: string;
}

export const AcademyLiveEngagementStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyLiveEngagementState>(initialAcademyLiveEngagementState),
  withReqState(...ALL_OPS),
  withMethods(
    (
      store,
      attendance = inject(LiveAttendanceService),
      subscriptions = inject(LiveSubscriptionsService),
      ratings = inject(LiveRatingsService),
      liveEvents = inject(LiveEventsService),
      errorStore = inject(ErrorStore),
    ) => {
      /** `POST /LiveAttendances/{id}/vod/join` — marks the VOD session open. */
      const vodJoin = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('vodJoin'))),
          switchMap((liveEventId) =>
            attendance.vodJoin(liveEventId).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      vodSessionByEvent: {
                        ...store.vodSessionByEvent(),
                        [liveEventId]: true,
                      },
                    },
                    setPropLoaded('vodJoin'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('vodJoin', error));
                },
              }),
            ),
          ),
        ),
      );

      /**
       * `POST /LiveAttendances/{id}/vod/progress` — monotonic: stores the max of the
       * existing and the reported `positionSeconds`, so progress only advances.
       */
      const reportVodProgress = rxMethod<ReportVodProgressInput>(
        pipe(
          tap(() => patchState(store, setPropLoading('reportVodProgress'))),
          switchMap(({ liveEventId, positionSeconds }) =>
            attendance.vodProgress(liveEventId, positionSeconds).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      vodProgressByEvent: {
                        ...store.vodProgressByEvent(),
                        [liveEventId]: Math.max(
                          store.vodProgressByEvent()[liveEventId] ?? 0,
                          positionSeconds,
                        ),
                      },
                    },
                    setPropLoaded('reportVodProgress'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('reportVodProgress', error));
                },
              }),
            ),
          ),
        ),
      );

      /**
       * `GET /LiveAttendances/{id}/resume` — resume position is the max over sessions.
       */
      const getResume = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('getResume'))),
          switchMap((liveEventId) =>
            attendance.resume(liveEventId).pipe(
              tapResponse({
                next: (res) => {
                  const seconds = res.seconds ?? res.Seconds ?? res.WatchedSeconds ?? 0;
                  patchState(
                    store,
                    {
                      resumeByEvent: {
                        ...store.resumeByEvent(),
                        [liveEventId]: Math.max(
                          store.resumeByEvent()[liveEventId] ?? 0,
                          seconds,
                        ),
                      },
                    },
                    setPropLoaded('getResume'),
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('getResume', error));
                },
              }),
            ),
          ),
        ),
      );

      /** `POST /LiveSubscriptions/{id}/subscribe` — idempotent; stores the subscription. */
      const subscribe = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('subscribe'))),
          switchMap((liveEventId) =>
            subscriptions.subscribe(liveEventId).pipe(
              tapResponse({
                next: (sub) =>
                  patchState(
                    store,
                    {
                      subscriptionByEvent: {
                        ...store.subscriptionByEvent(),
                        [liveEventId]: sub,
                      },
                    },
                    setPropLoaded('subscribe'),
                  ),
                // ⚠ 500 (event Ended/Cancelled/Recorded) surfaces raw; consumer maps "event closed".
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('subscribe', error));
                },
              }),
            ),
          ),
        ),
      );

      /** `DELETE /LiveSubscriptions/{id}/subscribe` — clears the stored subscription. */
      const unsubscribe = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('unsubscribe'))),
          switchMap((liveEventId) =>
            subscriptions.unsubscribe(liveEventId).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      subscriptionByEvent: {
                        ...store.subscriptionByEvent(),
                        [liveEventId]: null,
                      },
                    },
                    setPropLoaded('unsubscribe'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('unsubscribe', error));
                },
              }),
            ),
          ),
        ),
      );

      /** `POST /LiveRatings/{id}/rate` — upsert the viewer's 1–5 rating. */
      const rate = rxMethod<RateInput>(
        pipe(
          tap(() => patchState(store, setPropLoading('rate'))),
          switchMap(({ liveEventId, score, comment }) =>
            ratings.rate(liveEventId, score, comment).pipe(
              tapResponse({
                next: (rating) =>
                  patchState(
                    store,
                    { ratingByEvent: { ...store.ratingByEvent(), [liveEventId]: rating } },
                    setPropLoaded('rate'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('rate', error));
                },
              }),
            ),
          ),
        ),
      );

      /**
       * `GET /LiveEvents/{id}/attendances` — the producer's participants roster (backend
       * 013): cross-tenant, with `PersonName`, open sessions first. One page of
       * `ATTENDANCES_PAGE_SIZE`; `Total` is kept to tell when the page is cut.
       */
      const loadAttendances = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAttendances'))),
          switchMap((liveEventId) =>
            liveEvents
              .attendances(liveEventId, { top: ATTENDANCES_PAGE_SIZE })
              .pipe(
                tapResponse({
                  next: (page) =>
                    patchState(
                      store,
                      {
                        attendancesByEvent: {
                          ...store.attendancesByEvent(),
                          [liveEventId]: page.items,
                        },
                        attendancesTotalByEvent: {
                          ...store.attendancesTotalByEvent(),
                          [liveEventId]: page.total,
                        },
                      },
                      setPropLoaded('loadAttendances'),
                    ),
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('loadAttendances', error));
                  },
                }),
              ),
          ),
        ),
      );

      /** Resets all engagement state and request lifecycles. */
      const reset = (): void =>
        patchState(store, initialAcademyLiveEngagementState, setPropInit(...ALL_OPS));

      return {
        vodJoin,
        reportVodProgress,
        getResume,
        subscribe,
        unsubscribe,
        rate,
        loadAttendances,
        reset,
      };
    },
  ),
);
